import React, {
  useEffect,
  useState,
} from "react";

import {
  getAuth,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";

const API_BASE =
  process.env.REACT_APP_SELLER_API ||
  "https://gallery.pitics.com";

export default function SellerSettings() {
  const auth =
    getAuth();

  const [user, setUser] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [profile, setProfile] =
    useState(null);

  const [deleting, setDeleting] =
    useState(false);

  const [message, setMessage] =
    useState("");

  // Seller details editor state must be declared inside the component.
  const [detailsForm, setDetailsForm] = useState({
    phone: "",
    businessName: "",
    accountNumber: "",
    ifscCode: "",
    gstNumber: "",
    panNumber: "",
  });
  const [editingDetails, setEditingDetails] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);

  /* =====================================
     SAFE JSON
  ===================================== */
  const safeJson =
    async (res) => {
      const txt =
        await res.text();

      try {
        return JSON.parse(
          txt
        );
      } catch {
        throw new Error(
          "Invalid server response"
        );
      }
    };

  /* =====================================
     LOAD USER + PROFILE
  ===================================== */
  useEffect(() => {
    const unsub =
      onAuthStateChanged(
        auth,
        async (u) => {
          if (!u) {
            setLoading(
              false
            );
            return;
          }

          setUser(u);

          try {
            const token =
              await u.getIdToken();

            const res =
              await fetch(
                `${API_BASE}/api/seller/me`,
                {
                  headers: {
                    "X-Firebase-Authorization":
                      token,
                  },
                }
              );

            const data =
              await safeJson(
                res
              );

            if (
              !data.success
            ) {
              throw new Error(
                data.error ||
                  "Failed to load profile"
              );
            }

            setProfile(data);
            setDetailsForm({
              phone: data?.seller?.phone || data?.request?.phone || "",
              businessName: data?.seller?.businessName || data?.request?.businessName || "",
              accountNumber: data?.seller?.accountNumber || data?.request?.accountNumber || "",
              ifscCode: data?.seller?.ifscCode || data?.request?.ifscCode || "",
              gstNumber: data?.seller?.gstNumber || data?.request?.gstNumber || "",
              panNumber: data?.seller?.panNumber || data?.request?.panNumber || ""
            });
          } catch (err) {
            setMessage(
              err.message
            );
          } finally {
            setLoading(
              false
            );
          }
        }
      );

    return () => unsub();
  }, [auth]);

  /* =====================================
     DELETE ACCOUNT
  ===================================== */
  const deleteAccount =
    async () => {
      const ok =
        window.confirm(
          "Delete seller account and all seller products permanently?"
        );

      if (!ok)
        return;

      try {
        setDeleting(
          true
        );
        setMessage("");

        const token =
          await auth.currentUser.getIdToken();

        const res =
          await fetch(
            `${API_BASE}/api/seller/delete-account`,
            {
              method:
                "POST",
              headers:
                {
                  "X-Firebase-Authorization":
                    token,
                },
            }
          );

        const data =
          await safeJson(
            res
          );

        if (
          !data.success
        ) {
          throw new Error(
            data.error ||
              "Delete failed"
          );
        }

        alert(
          "Seller account deleted successfully."
        );

        await signOut(
          auth
        );

        window.close();
      } catch (err) {
        setMessage(
          err.message
        );
      } finally {
        setDeleting(
          false
        );
      }
    };

  /* =====================================
     UI STYLES
  ===================================== */
  const page = {
    minHeight:
      "100vh",
    background:
      "linear-gradient(180deg,#eef2ff,#ffffff)",
    padding: 28,
    fontFamily:
      "Inter, Arial, sans-serif",
  };

  const wrap = {
    maxWidth: 980,
    margin:
      "0 auto",
  };

  const card = {
    background:
      "#ffffff",
    borderRadius: 28,
    padding: 28,
    boxShadow:
      "0 18px 50px rgba(0,0,0,.08)",
    marginBottom: 22,
  };

  const row = {
    display:
      "grid",
    gridTemplateColumns:
      "220px 1fr",
    gap: 16,
    padding:
      "10px 0",
    borderBottom:
      "1px solid #f1f5f9",
  };

  const label = {
    fontWeight: 900,
    color:
      "#374151",
  };

  const value = {
    color:
      "#111827",
    wordBreak:
      "break-word",
  };

  const btn = {
    border: "none",
    padding:
      "14px 20px",
    borderRadius: 16,
    cursor:
      "pointer",
    fontWeight: 900,
  };

  const saveSellerDetails = async (e) => {
    e.preventDefault();
    if (!/^[6-9]\d{9}$/.test(detailsForm.phone.trim())) {
      setMessage("Enter a valid 10-digit Indian mobile number.");
      return;
    }
    if (detailsForm.accountNumber && !/^\d{9,18}$/.test(detailsForm.accountNumber.trim())) {
      setMessage("Bank account number must contain 9–18 digits.");
      return;
    }
    if (detailsForm.ifscCode && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(detailsForm.ifscCode.trim().toUpperCase())) {
      setMessage("Enter a valid IFSC code.");
      return;
    }
    try {
      setSavingDetails(true);
      setMessage("");
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(`${API_BASE}/api/seller/update-details`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Firebase-Authorization": token },
        body: JSON.stringify({
          phone: detailsForm.phone.trim(),
          businessName: detailsForm.businessName.trim(),
          accountNumber: detailsForm.accountNumber.trim(),
          ifscCode: detailsForm.ifscCode.trim().toUpperCase(),
          gstNumber: detailsForm.gstNumber.trim().toUpperCase(),
          panNumber: detailsForm.panNumber.trim().toUpperCase()
        })
      });
      const data = await safeJson(res);
      if (!res.ok || !data.success) throw new Error(data.error || "Unable to update seller details.");
      setProfile((prev) => ({ ...prev, ...data, seller: { ...(prev?.seller || {}), ...(data.seller || data.details || detailsForm) } }));
      setEditingDetails(false);
      setMessage("Seller details updated successfully.");
    } catch (err) {
      setMessage(err.message || "Failed to update seller details.");
    } finally {
      setSavingDetails(false);
    }
  };

  if (loading) {
    return (
      <div className="seller-portal-page seller-settings-page" style={page}>
        <div style={wrap}>
          <div style={card}>
            Loading settings...
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="seller-portal-page seller-settings-page" style={page}>
        <div style={wrap}>
          <div style={card}>
            Please login.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="seller-portal-page seller-settings-page" style={page}>
      <div style={wrap}>
        {/* HEADER */}
        <div
          style={{
            ...card,
            display:
              "flex",
            justifyContent:
              "space-between",
            alignItems:
              "center",
            gap: 20,
            flexWrap:
              "wrap",
          }}
        >
          <div
            style={{
              display:
                "flex",
              alignItems:
                "center",
              gap: 16,
            }}
          >
            <img
              src={
                user.photoURL ||
                "https://via.placeholder.com/70"
              }
              alt="profile"
              style={{
                width: 70,
                height: 70,
                borderRadius:
                  "50%",
                objectFit:
                  "cover",
                border:
                  "3px solid #e5e7eb",
              }}
            />

            <div>
              <h1
                style={{
                  margin:
                    "0 0 6px 0",
                }}
              >
                Seller Settings
              </h1>

              <div
                style={{
                  color:
                    "#6b7280",
                }}
              >
                Manage your seller account
              </div>
            </div>
          </div>

          <button
            style={{
              ...btn,
              background:
                "#eef2ff",
            }}
            onClick={() =>
              signOut(
                auth
              )
            }
          >
            Sign Out
          </button>
        </div>

        {/* EDIT SELLER DETAILS */}
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <h2 style={{ marginTop: 0 }}>Update Seller Details</h2>
            <button type="button" style={{ ...btn, background: "#2563eb", color: "#fff" }} onClick={() => setEditingDetails((v) => !v)}>
              {editingDetails ? "Cancel Edit" : "Edit Mobile / Bank Details"}
            </button>
          </div>
          {editingDetails && <form onSubmit={saveSellerDetails} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12 }}>
            {[
              ["phone", "Mobile number", "tel"],
              ["businessName", "Business name", "text"],
              ["accountNumber", "Bank account number", "text"],
              ["ifscCode", "IFSC code", "text"],
              ["gstNumber", "GST number", "text"],
              ["panNumber", "PAN number", "text"]
            ].map(([key, label, type]) => <label key={key} style={{ display: "block", fontWeight: 700, color: "#374151" }}>
              {label}
              <input type={type} value={detailsForm[key] || ""} onChange={(e) => setDetailsForm((prev) => ({ ...prev, [key]: key === "phone" || key === "accountNumber" ? e.target.value.replace(/\D/g, "").slice(0, key === "phone" ? 10 : 18) : key === "ifscCode" || key === "gstNumber" || key === "panNumber" ? e.target.value.toUpperCase() : e.target.value }))} style={{ width: "100%", boxSizing: "border-box", padding: 12, marginTop: 6, border: "1px solid #cbd5e1", borderRadius: 10 }} />
            </label>)}
            <button disabled={savingDetails} type="submit" style={{ ...btn, background: "#16a34a", color: "#fff", alignSelf: "end" }}>{savingDetails ? "Saving..." : "Save Details"}</button>
          </form>}
          {message && <p role="status" style={{ color: message.toLowerCase().includes("success") ? "#15803d" : "#b91c1c" }}>{message}</p>}
          <small style={{ color: "#64748b" }}>Changes are saved securely by the seller API. Bank detail changes may require verification.</small>
        </div>

        {/* PROFILE */}
        <div style={card}>
          <h2
            style={{
              marginTop: 0,
            }}
          >
            Profile
          </h2>

          <div style={row}>
            <div style={label}>
              Name
            </div>
            <div style={value}>
              {
  profile?.seller?.name ||

  profile?.request?.name ||

  user.displayName ||

  ""
}
            </div>
          </div>

          <div style={row}>
            <div style={label}>
              Email
            </div>
            <div style={value}>
              {
  profile?.seller?.email ||

  profile?.request?.email ||

  user.email
}
            </div>
          </div>

          <div style={row}>
            <div style={label}>
              Phone
            </div>
            <div style={value}>
              {
  profile?.seller?.phone ||

  profile?.request?.phone ||

  "-"
}
            </div>
          </div>

          <div style={row}>
            <div style={label}>
              Business Name
            </div>
            <div style={value}>
              {profile?.seller?.businessName ||
profile?.request?.businessName}
            </div>
          </div>

          <div style={row}>
            <div style={label}>
              Status
            </div>
            <div style={value}>
              {profile?.sellerActive
                ? "Active Seller"
                : profile?.requestStatus ||
                  "Pending"}
            </div>
          </div>
        </div>

        {/* BANK / TAX */}
        <div style={card}>
          <h2
            style={{
              marginTop: 0,
            }}
          >
            Banking & Tax
          </h2>

          <div style={row}>
            <div style={label}>
              Account Number
            </div>
            <div style={value}>
              {
  profile?.seller?.accountNumber ||

  profile?.request?.accountNumber ||

  "-"
}
            </div>
          </div>

          <div style={row}>
            <div style={label}>
              IFSC Code
            </div>
            <div style={value}>
              {
  profile?.seller?.ifscCode ||

  profile?.request?.ifscCode ||

  "-"
}
            </div>
          </div>

          <div style={row}>
            <div style={label}>
              GST Number
            </div>
            <div style={value}>
              {
  profile?.seller?.gstNumber ||

  profile?.request?.gstNumber ||

  "-"
}
            </div>
          </div>

          <div style={row}>
            <div style={label}>
              PAN Number
            </div>
            <div style={value}>
              {
  profile?.seller?.panNumber ||

  profile?.request?.panNumber ||

  "-"
}
            </div>
          </div>
        </div>

        {/* ADDRESS */}
        <div style={card}>
          <h2
            style={{
              marginTop: 0,
            }}
          >
            Address
          </h2>

          <div style={value}>
  {
    [
      profile?.seller?.address1 ||
        profile?.request?.address1,

      profile?.seller?.address2 ||
        profile?.request?.address2,

      profile?.seller?.address3 ||
        profile?.request?.address3,

      profile?.seller?.address4 ||
        profile?.request?.address4,

      profile?.seller?.pincode ||
        profile?.request?.pincode
    ]
      .filter(Boolean)
      .join(", ")
  }
</div>
        </div>

        {/* DELETE */}
        <div
          style={{
            ...card,
            background:
              "#fef2f2",
            border:
              "1px solid #fecaca",
          }}
        >
          <h2
            style={{
              marginTop: 0,
              color:
                "#991b1b",
            }}
          >
            Danger Zone
          </h2>

          <p
            style={{
              color:
                "#7f1d1d",
              lineHeight:
                1.7,
            }}
          >
            Deleting your
            seller account
            will also delete
            all your seller
            products.
          </p>

          <button
            style={{
              ...btn,
              background:
                "linear-gradient(135deg,#dc2626,#ef4444)",
              color:
                "#fff",
            }}
            onClick={
              deleteAccount
            }
            disabled={
              deleting
            }
          >
            {deleting
              ? "Deleting..."
              : "🗑 Delete Seller Account"}
          </button>
        </div>

        {message && (
          <div style={card}>
            {message}
          </div>
        )}
      </div>
    </div>
  );


}